export class ChatAPI {
  constructor(config) {
    this.config = config;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.baseDelay = 1000; // 1 second
    this.inflight = false;
  }

  async sendMessage(message) {
    if (this.config.mode === 'demo' && this.inflight) {
      throw new Error('A request is already in flight for this session.');
    }

    const payload = {
      sessionId: this.config.sessionId,
      greetingMessage: this.config.greetingMessage,
      content: message,
      type: 'message'
    };

    if (this.config.mode !== 'demo') {
      payload.clientId = this.config.clientId;
    }

    if (this.config.mode === 'demo') {
      this.inflight = true;
    }

    try {
      return await this.makeRequest(payload);
    } finally {
      if (this.config.mode === 'demo') {
        this.inflight = false;
      }
    }
  }

  getRequestUrl() {
    const endpoint = this.config.apiEndpoint;
    if (this.config.mode !== 'demo') return endpoint;
    // Derive {base}/chat/demo from the configured /chat endpoint
    return endpoint.replace(/\/chat\/?$/, '') + '/chat/demo';
  }

  async buildHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    if (this.config.mode === 'demo') {
      let token;
      try {
        token = await this.config.getToken();
      } catch (err) {
        this.handleTerminalAuth('getToken threw');
        throw new Error('Authentication required.');
      }
      if (!token) {
        this.handleTerminalAuth('empty token');
        throw new Error('Authentication required.');
      }
      headers['Authorization'] = `Bearer ${token}`;
      return headers;
    }

    if (this.config.siteKey) {
      headers['x-site-key'] = this.config.siteKey;
    } else {
      console.warn('Chatbot Widget: No site key found in config');
    }
    return headers;
  }

  async makeRequest(payload, attempt = 0, didAuthRetry = false) {
    try {
      const headers = await this.buildHeaders();

      const response = await fetch(this.getRequestUrl(), {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (response.status === 401 && this.config.mode === 'demo' && !didAuthRetry) {
        // One-shot re-auth: fetch a fresh token and retry with same sessionId.
        return this.makeRequest(payload, attempt, true);
      }

      if (response.status === 401 && this.config.mode === 'demo') {
        this.handleTerminalAuth('401 after refresh');
        throw new Error('Authentication required.');
      }

      if (response.status === 404 && this.config.mode === 'demo') {
        this.handleOnboardingIncomplete();
        throw new Error('Demo not ready. Please finish onboarding.');
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      const content = data && (typeof data.content === 'string' ? data.content : (typeof data.body === 'string' ? data.body : null));
      if (content == null) {
        throw new Error('Invalid response format from server');
      }

      return {
        content,
        lead_saved: data.lead_saved || false
      };
    } catch (error) {
      if (attempt < this.maxRetries && this.isRetryableError(error)) {
        const delay = this.calculateDelay(attempt);
        console.warn(`Request failed, retrying in ${delay}ms... (attempt ${attempt + 1}/${this.maxRetries})`, error);

        await this.sleep(delay);
        return this.makeRequest(payload, attempt + 1, didAuthRetry);
      }

      console.error('Chat API request failed:', error);
      throw new Error(this.getUserFriendlyError(error));
    }
  }

  handleTerminalAuth(reason) {
    console.warn('Chatbot Widget: terminal auth failure —', reason);
    try {
      window.dispatchEvent(new CustomEvent('leadblaze:auth_required', {
        bubbles: true,
        composed: true,
        detail: {
          sessionId: this.config.sessionId,
          clientId: this.config.clientId,
          reason
        }
      }));
    } catch (e) {}
    try {
      this.config.onAuthRequired && this.config.onAuthRequired({ reason });
    } catch (e) {
      console.error('Chatbot Widget: onAuthRequired callback threw:', e);
    }
  }

  handleOnboardingIncomplete() {
    console.warn('Chatbot Widget: demo onboarding incomplete (404)');
    try {
      window.dispatchEvent(new CustomEvent('leadblaze:onboarding_incomplete', {
        bubbles: true,
        composed: true,
        detail: {
          sessionId: this.config.sessionId,
          clientId: this.config.clientId
        }
      }));
    } catch (e) {}
    try {
      this.config.onOnboardingIncomplete && this.config.onOnboardingIncomplete();
    } catch (e) {
      console.error('Chatbot Widget: onOnboardingIncomplete callback threw:', e);
    }
  }

  isRetryableError(error) {
    // Retry on network errors and 5xx server errors (not on auth/onboarding errors)
    if (error.message === 'Authentication required.' || error.message === 'Demo not ready. Please finish onboarding.') {
      return false;
    }
    return error.name === 'TypeError' || // Network errors
           error.message.includes('fetch') ||
           (error.message.includes('HTTP 5') && error.message.includes('HTTP 50')) ||
           error.message.includes('HTTP 502') ||
           error.message.includes('HTTP 503') ||
           error.message.includes('HTTP 504');
  }

  calculateDelay(attempt) {
    // Exponential backoff with jitter
    const exponentialDelay = this.baseDelay * Math.pow(2, attempt);
    const jitter = Math.random() * 0.1 * exponentialDelay;
    return Math.min(exponentialDelay + jitter, 10000); // Cap at 10 seconds
  }

  getUserFriendlyError(error) {
    if (error.message === 'Authentication required.') {
      return 'Please sign in again to continue.';
    }

    if (error.message === 'Demo not ready. Please finish onboarding.') {
      return 'Demo not ready. Please finish onboarding.';
    }

    if (error.message.includes('HTTP 401') || error.message.includes('HTTP 403')) {
      return 'Access denied. This domain is not authorized to use this service.';
    }

    if (error.message.includes('HTTP 429')) {
      return 'Too many requests. Please wait a moment and try again.';
    }

    if (error.message.includes('HTTP 4')) {
      return 'Invalid request. Please check your configuration.';
    }

    if (error.message.includes('HTTP 5') || error.name === 'TypeError') {
      return 'Service temporarily unavailable. Please try again.';
    }

    return 'An unexpected error occurred. Please try again.';
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
